import { SupabaseClient } from "@supabase/supabase-js";

/**
 * List all unique keys for stored static content.
 *
 * @param client - Supabase client
 * @returns Array of unique content keys
 */
export async function getStaticContentKeys(client: SupabaseClient): Promise<string[]> {
  const { data, error } = await client
    .from("static_content")
    .select("id")
    .order("id");
  if (error) throw error;
  return [...new Set(data.map((row: any) => row.id))];
}

/**
 * Retrieve the latest version of a static content entry by key.
 *
 * @param client - Supabase client
 * @param key - Identifier for the content
 * @returns Content value and version
 */
export async function getStaticContentByKey(
  client: SupabaseClient,
  key: string
): Promise<{ id: string; value: string; version: number }> {
  const { data, error } = await client
    .from("static_content")
    .select("*")
    .eq("id", key)
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) {
    throw new Error(`No static content found for key: ${key}`);
  }
  return {
    id: data.id,
    value: data.value,
    version: data.version,
  };
}

/**
 * Save a new version of a static content entry.
 *
 * Automatically increments the version number.
 *
 * @param client - Supabase client
 * @param id - Content key
 * @param value - Content body
 * @returns Saved content with version number
 */
export async function saveStaticContent(
  client: SupabaseClient,
  id: string,
  value: string
): Promise<{ id: string; value: string; version: number }> {
  const { data: versionData, error: versionError } = await client
    .from("static_content")
    .select("version")
    .eq("id", id)
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (versionError) throw versionError;
  const nextVersion = versionData ? versionData.version + 1 : 1;
  const { data, error } = await client
    .from("static_content")
    .insert([{ id, value, version: nextVersion }])
    .select("*")
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) {
    throw new Error(`No static content found for key: ${id}`);
  }
  return {
    id: data.id,
    value: data.value,
    version: data.version,
  };
}
